import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { Autocomplete, AutocompleteItem, Input } from '@nextui-org/react';
import { CtyButton, ListValueSelect } from '@circuitry-ai/doc-ui';
import { MetadataDefinition, useGetMetadataDefinitionsQuery } from '@circuitry-ai/doc-data';
import { useTranslation } from 'react-i18next';

export interface UserAttribute {
  key: string;
  value: string;
}

// List-type attribute values are stored as a single comma-separated string to match the user_metadata Record<string, string> payload.
const listValueToString = (values: string[]): string => values.join(', ');
const stringToListValue = (value: string): string[] =>
  value ? value.split(',').map((v) => v.trim()).filter(Boolean) : [];

// Pins already-selected values into the option list even if they've since been removed from (or never matched) the definition's catalog, so previously saved data still renders instead of appearing blank.
const getListOptions = (definitionOptions: string[] | null | undefined, currentValue: string): string[] => {
  const baseOptions = definitionOptions ?? [];
  const selectedValues = stringToListValue(currentValue);
  const missing = selectedValues.filter((v) => !baseOptions.includes(v));
  return missing.length > 0 ? [...baseOptions, ...missing] : baseOptions;
};

interface UserAttributesEditorProps {
  readonly attributes: UserAttribute[];
  readonly onChange: (attributes: UserAttribute[]) => void;
  readonly maxAttributes?: number;
}

export function userMetadataToAttributes(metadata?: Record<string, string>): UserAttribute[] {
  if (!metadata || typeof metadata !== 'object') return [];
  return Object.entries(metadata).map(([key, value]) => ({ key, value: String(value ?? '') }));
}

export function attributesToUserMetadata(attributes: UserAttribute[]): Record<string, string> {
  const metadata: Record<string, string> = {};
  for (const attr of attributes) {
    const trimmedKey = attr.key.trim();
    if (trimmedKey) {
      metadata[trimmedKey] = attr.value;
    }
  }
  return metadata;
}

export function getMetadataDiff(
  original: Record<string, string>,
  current: UserAttribute[]
): Record<string, string | null> {
  const diff: Record<string, string | null> = {};
  const currentMap = attributesToUserMetadata(current);

  for (const key of Object.keys(original)) {
    if (!(key in currentMap)) {
      diff[key] = null;
    } else if (currentMap[key] !== original[key]) {
      diff[key] = currentMap[key];
    }
  }

  for (const key of Object.keys(currentMap)) {
    if (!(key in original)) {
      diff[key] = currentMap[key];
    }
  }

  return diff;
}

export function UserAttributesEditor({ attributes, onChange, maxAttributes = 10 }: UserAttributesEditorProps) {
  const { t } = useTranslation();
  const [newAttr, setNewAttr] = useState<UserAttribute>({ key: '', value: '' });
  const [newAttrErrors, setNewAttrErrors] = useState<{ key?: string; value?: string }>({});
  // NextUI's Autocomplete keeps its own displayed text independent of the selectedKey prop, so clearing selectedKey alone won't clear what's shown — remounting via this counter forces a real reset after Add.
  const [newAttrResetKey, setNewAttrResetKey] = useState(0);
  const { data: metadataDefinitionsData } = useGetMetadataDefinitionsQuery(undefined, { refetchOnMountOrArgChange: true });

  const isAllowedToAdd = attributes.length < maxAttributes;

  const activeAttributeDefinitions = useMemo(
    () => (metadataDefinitionsData?.data ?? []).filter((def: MetadataDefinition) => !def.status || def.status === 'active'),
    [metadataDefinitionsData]
  );

  const attributeDefinitionsByName = useMemo(
    () => new Map((metadataDefinitionsData?.data ?? []).map((def: MetadataDefinition) => [def.name, def])),
    [metadataDefinitionsData]
  );

  const usedAttributeKeys = useMemo(
    () => new Set(attributes.map((a) => a.key).filter(Boolean)),
    [attributes]
  );

  const getAttributeOptionsForRow = (currentKey: string) => {
    const options = activeAttributeDefinitions.filter(
      (def: MetadataDefinition) => def.name === currentKey || !usedAttributeKeys.has(def.name)
    );
    if (currentKey && !options.some((def: MetadataDefinition) => def.name === currentKey)) {
      return [{ name: currentKey }, ...options];
    }
    return options;
  };

  const handleAdd = () => {
    const errors: { key?: string; value?: string } = {};
    if (!newAttr.key.trim()) errors.key = t('Name is required');
    if (!newAttr.value.trim()) errors.value = t('Value is required');
    if (attributes.some(a => a.key === newAttr.key.trim())) errors.key = t('Attribute already exists');

    if (Object.keys(errors).length > 0) {
      setNewAttrErrors(errors);
      return;
    }

    onChange([...attributes, { key: newAttr.key.trim(), value: newAttr.value.trim() }]);
    setNewAttr({ key: '', value: '' });
    setNewAttrErrors({});
    setNewAttrResetKey((k) => k + 1);
  };

  const handleRemove = (index: number) => {
    onChange(attributes.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: 'key' | 'value', val: string) => {
    const updated = attributes.map((attr, i) => i === index ? { ...attr, [field]: val } : attr);
    onChange(updated);
  };

  const handleRowKeyChange = (index: number, newKey: string) => {
    const updated = attributes.map((attr, i) => (i === index ? { key: newKey, value: '' } : attr));
    onChange(updated);
  };

  const handleNewAttrKeyChange = (newKey: string) => {
    setNewAttr({ key: newKey, value: '' });
    setNewAttrErrors((prev) => ({ ...prev, key: undefined, value: undefined }));
  };

  const newAttrDefinition = attributeDefinitionsByName.get(newAttr.key);

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-medium text-foreground">{t('User Attributes')}</h3>
        <p className="text-sm text-default-500">{t('Manage custom metadata attributes for this user')}</p>
      </div>

      {isAllowedToAdd && (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-default-600">{t('Add attributes')}</label>
          <div className="flex gap-2">
            <Autocomplete
              key={newAttrResetKey}
              size="sm"
              variant="bordered"
              defaultSelectedKey={newAttr.key || undefined}
              onSelectionChange={(key) => handleNewAttrKeyChange(key ? String(key) : '')}
              placeholder={t('Attribute Type')}
              aria-label={t('Attribute Type')}
              isInvalid={!!newAttrErrors.key}
              errorMessage={newAttrErrors.key}
            >
              {getAttributeOptionsForRow(newAttr.key).map((def) => (
                <AutocompleteItem key={def.name} textValue={def.name}>
                  {def.name}
                </AutocompleteItem>
              ))}
            </Autocomplete>
            {newAttrDefinition?.type === 'list' ? (
              <ListValueSelect
                key={newAttr.key}
                options={getListOptions(newAttrDefinition.value, newAttr.value)}
                value={stringToListValue(newAttr.value)}
                onChange={(values) => { setNewAttr(prev => ({ ...prev, value: listValueToString(values) })); setNewAttrErrors(prev => ({ ...prev, value: undefined })); }}
                placeholder={t('Attribute Value')}
                ariaLabel={t('Attribute Value')}
                isInvalid={!!newAttrErrors.value}
                errorMessage={newAttrErrors.value}
                isDisabled={!newAttr.key}
              />
            ) : (
              <Input
                key={newAttr.key}
                type={newAttrDefinition?.type === 'number' ? 'number' : 'text'}
                size="sm"
                variant="bordered"
                value={newAttr.value}
                onValueChange={(val) => { setNewAttr(prev => ({ ...prev, value: val })); setNewAttrErrors(prev => ({ ...prev, value: undefined })); }}
                placeholder={t('Attribute Value')}
                isInvalid={!!newAttrErrors.value}
                errorMessage={newAttrErrors.value}
              />
            )}
            <CtyButton radius="sm" onPress={handleAdd} size="sm" className="border-1 border-default-300 h-[32px]" variant="light">
              {t('Add')}
            </CtyButton>
          </div>
        </div>
      )}

      {attributes.map((attr, index) => {
        const rowDefinition = attributeDefinitionsByName.get(attr.key);
        return (
          <div key={`${attr.key}-${index}`} className="flex gap-2 items-start">
            <Autocomplete
              size="sm"
              variant="bordered"
              label={t('Attribute Type')}
              labelPlacement="outside"
              selectedKey={attr.key || null}
              onSelectionChange={(key) => handleRowKeyChange(index, key ? String(key) : '')}
              placeholder={t('Attribute Type')}
              aria-label={t('Attribute Type')}
            >
              {getAttributeOptionsForRow(attr.key).map((def) => (
                <AutocompleteItem key={def.name} textValue={def.name}>
                  {def.name}
                </AutocompleteItem>
              ))}
            </Autocomplete>
            {rowDefinition?.type === 'list' ? (
              <div className="flex flex-col gap-1.5 w-full">
                <label className="text-sm text-default-600">{t('Attribute Value')}</label>
                <ListValueSelect
                  key={attr.key}
                  options={getListOptions(rowDefinition.value, attr.value)}
                  value={stringToListValue(attr.value)}
                  onChange={(values) => handleChange(index, 'value', listValueToString(values))}
                  placeholder={t('Attribute Value')}
                  ariaLabel={t('Attribute Value')}
                />
              </div>
            ) : (
              <Input
                key={attr.key}
                type={rowDefinition?.type === 'number' ? 'number' : 'text'}
                size="sm"
                variant="bordered"
                label={t('Attribute Value')}
                labelPlacement="outside"
                value={attr.value}
                onValueChange={(val) => handleChange(index, 'value', val)}
                placeholder={t('Attribute Value')}
              />
            )}
            <div className="flex items-center mt-7">
              <Icon icon="hugeicons:delete-02" className="cursor-pointer" color="red" height={20} width={20} onClick={() => handleRemove(index)} />
            </div>
          </div>
        );
      })}

      {attributes.length === 0 && (
        <p className="text-sm text-default-400 italic">{t('No attributes added yet')}</p>
      )}
    </div>
  );
}

export default UserAttributesEditor;
